import * as Yup from 'yup';

import { AlertEnum, ButtonTypeEnum, CheckboxTypeEnum, EstrategiaApuracaoEnum, InformationModalVariantEnum } from '../../enums';
import {
  Button,
  CheckboxField,
  DateTimeField,
  DefaultCard,
  DefaultContent,
  NumberField,
  SelectField,
  TextField,
} from '../../components';
import { Form, Formik, getIn } from 'formik';
import { useAlert, useAuthAxios, useErrorHandler, useModal } from '../../hooks';

import { ApiHelper } from '../../helpers';
import { EleicaoFormType } from '../../types';
import { EstrategiaApuracaoEnumType } from '../../types/enums/EstrategiaApuracaoType';
import { FaQuestion } from 'react-icons/fa';
import { FunctionComponent } from 'react';
import { IconButton } from '../forms';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const EleicaoSchema = (t: Function) =>
  Yup.object().shape({
    data_fim: Yup.date().required(t('validacao.campo_obrigatorio')),
    data_inicio: Yup.string().required(t('validacao.campo_obrigatorio')),
    estrategia_apuracao: Yup.string().required(t('validacao.campo_obrigatorio')),
    quantidade_votos_por_cargo: Yup.number()
      .required(t('validacao.campo_obrigatorio'))
      .min(1, t('validacao.quantidade_votos_por_cargo_invalido')),
    titulo: Yup.string().required(t('validacao.campo_obrigatorio')),
    titulo_urna: Yup.string().required(t('validacao.campo_obrigatorio')),
  });

const initialValues = {
  auditada: false,
  data_fim: '',
  data_inicio: '',
  estrategia_apuracao: '',
  quantidade_votos_por_cargo: 1,
  titulo: '',
  titulo_urna: '',
};

const EleicaoForm: FunctionComponent<EleicaoFormType> = ({ eleicao }) => {
  const axios = useAuthAxios();
  const errorHandler = useErrorHandler();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { createAlert } = useAlert();
  const isAlteracao = eleicao?.id;
  const { informationModal } = useModal();

  return (
    <Formik
      initialValues={{ ...initialValues, ...eleicao }}
      validationSchema={EleicaoSchema(t)}
      onSubmit={(values, { setSubmitting }) => {
        setTimeout(() => {
          axios(isAlteracao ? ApiHelper.getUrl.apiSCE.eleicao(eleicao.id) : ApiHelper.getUrl.apiSCE.eleicoes(), {
            data: values,
            method: isAlteracao ? 'PUT' : 'POST',
          })
            .then((response) => {
              navigate(`/eleicoes/${response.data.id!}`);
              createAlert(t(`sucesso.${isAlteracao ? 'alterar' : 'cadastrar'}_dados_gerais_eleicao`), AlertEnum.SUCCESS);
            })
            .catch((error) => errorHandler(error, t(`erro.${isAlteracao ? 'alterar' : 'cadastrar'}_dados_gerais_eleicao`)))
            .finally(() => setSubmitting(false));
        }, 400);
      }}
    >
      {(props) => (
        <Form>
          <DefaultContent
            title={t(`label.${isAlteracao ? 'alterar' : 'cadastrar'}_eleicao`)}
            pageAction={
              <Button
                title={t('label.voltar')}
                onClick={() => navigate(isAlteracao ? `/eleicoes/${eleicao.id}` : '/eleicoes')}
                outline
              />
            }
          >
            <DefaultCard
              cardTitle={t('label.dados_eleicao')}
              cardSubtitle={t('texto.cadastro_dados_gerais_eleicao')}
              helpLink={
                'https://conhecimento.sti.ufpb.br/books/sistema-de-cadastro-de-eleicoes/page/cadastrar-os-dados-gerais-da-eleicao'
              }
              footer={
                <div className="d-flex justify-content-center gap-5">
                  <Button
                    title={t('label.cancelar')}
                    onClick={() => navigate(isAlteracao ? `/eleicoes/${eleicao.id}` : '/eleicoes')}
                    disabled={props.isSubmitting || props.isValidating}
                    outline
                  />
                  <Button
                    title={t(`label.${isAlteracao ? 'alterar' : 'cadastrar'}`)}
                    type={ButtonTypeEnum.SUBMIT}
                    disabled={props.isSubmitting || props.isValidating}
                  />
                </div>
              }
            >
              <>
                <div className="mb-3">
                  <TextField formikProps={props} name="titulo" label={t('label.titulo_eleicao')} maxLength={100} required />
                </div>
                <div className="mb-3">
                  <TextField formikProps={props} name="titulo_urna" label={t('label.titulo_urna')} maxLength={100} required />
                </div>
                <div className="row mb-3">
                  <div className="col">
                    <DateTimeField formikProps={props} name="data_inicio" label={t('label.data_inicio')} required />
                  </div>
                  <div className="col">
                    <DateTimeField formikProps={props} name="data_fim" label={t('label.data_fim')} required />
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col">
                    <NumberField
                      formikProps={props}
                      name="quantidade_votos_por_cargo"
                      label={t('label.quantidade_votos_por_cargo')}
                      maxLength={2}
                      required
                    />
                  </div>

                  <div className="col">
                    <SelectField
                      label={t('label.estrategia_apuracao_votos')}
                      name="estrategia_apuracao"
                      formikProps={props}
                      required
                    >
                      <option value="">{t('label.selecione_estrategia')}</option>
                      {EstrategiaApuracaoEnum.getAll().map((v: EstrategiaApuracaoEnumType) => (
                        <option value={v.key} key={v.key}>
                          {t(v.descricao)}
                        </option>
                      ))}
                    </SelectField>
                  </div>
                </div>
                <div>
                  <legend
                    className={classNames(
                      'form-label',
                      getIn(props.touched, 'auditoria') && getIn(props.errors, 'auditoria') && 'text-danger',
                    )}
                  >
                    {t('label.auditoria')}
                    <IconButton
                      Icon={FaQuestion}
                      title={t('label.ajuda')}
                      circle
                      className="d-inline-flex ms-1"
                      onClick={() => {
                        informationModal(
                          <div>
                            <p>{t('texto.modal_eleicao_auditada_parte_1')}</p>
                            <div
                              className="alert alert-info"
                              dangerouslySetInnerHTML={{ __html: t('texto.modal_eleicao_auditada_parte_2') }}
                            />
                          </div>,
                          t('label.como_funciona_uma_eleicao_auditada'),
                          InformationModalVariantEnum.INFO,
                        );
                      }}
                    />
                  </legend>
                  <fieldset
                    className={classNames(
                      'p-2 border rounded',
                      getIn(props.touched, 'auditoria') && getIn(props.errors, 'auditoria') && 'border-danger',
                    )}
                  >
                    <CheckboxField
                      formikProps={props}
                      label={t('label.habilitar')}
                      name="auditada"
                      type={CheckboxTypeEnum.SWITCH}
                    />
                  </fieldset>
                </div>
              </>
            </DefaultCard>
          </DefaultContent>
        </Form>
      )}
    </Formik>
  );
};

export default EleicaoForm;
